import { apiClient } from "@/shared/services/api";
import { randomId } from "@/shared/utils/randomId";

interface ApiResponse<T> {
  success: boolean;
  message: string | null;
  errorCode: string | null;
  errors: Record<string, string[]> | null;
  data: T;
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in (payload as ApiResponse<T>) &&
    "success" in (payload as ApiResponse<T>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

// ── Types mirror the backend Common.DTOs.WalletDtos ─────────────────────────

export interface Wallet {
  id: string;
  customerId: string;
  customerName: string;
  isActive: boolean;
  spendableBalance: number;
  pendingBalance: number;
  lastComputedAt?: string | null;
}

export interface WalletLedgerEntry {
  id: string;
  customerWalletId: string;
  customerId: string;
  customerName: string;
  direction: "Credit" | "Debit";
  type: "Overpayment" | "Topup" | "Refund" | "ManualAdjustment" | "AppliedToOrder" | "CashOut";
  amount: number;
  status: "Pending" | "Approved" | "Rejected" | "Reversed";
  orderId?: string | null;
  orderNumber?: string | null;
  pairedPaymentId?: string | null;
  mpesaTransactionId?: string | null;
  payoutChannel?: "Cash" | "MPesa" | "BankTransfer" | null;
  payoutReference?: string | null;
  reason?: string | null;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  rejectionReason?: string | null;
  reversesEntryId?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
  cause?: string | null;
}

export interface CreateWalletTopupRequest {
  customerId: string;
  amount: number;
  channel: "Cash" | "MPesa" | "BankTransfer";
  payoutReference?: string | null;
  notes?: string | null;
}

export interface CreateWalletCashOutRequest {
  customerId: string;
  amount: number;
  channel: "Cash" | "MPesa" | "BankTransfer";
  notes?: string | null;
}

export interface CreateWalletAdjustmentRequest {
  customerId: string;
  amount: number;
  direction: "Credit" | "Debit";
  reason: string;
}

export interface ApplyWalletToOrderRequest {
  customerId: string;
  orderId: string;
  amount: number;
}

export interface ApproveWalletEntryRequest {
  approvalNotes?: string | null;
  payoutReference?: string | null;
  disbursementConfirmed: boolean;
}

export interface RejectWalletEntryRequest {
  rejectionReason: string;
}

export interface ReverseWalletEntryRequest {
  reason: string;
}

/** Numeric on the wire to match the C# enum used by CreatePaymentDto.SurplusDisposition. */
export const SURPLUS_DISPOSITION = {
  ReturnAsChange: 1,
  CreditToWallet: 2,
} as const;
export type SurplusDisposition = (typeof SURPLUS_DISPOSITION)[keyof typeof SURPLUS_DISPOSITION];

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Wallet write endpoints require an Idempotency-Key header. Use a fresh value
 * per logical user action (one submit click = one key). The same key replayed
 * with the same body returns the original response; with a different body it
 * returns 409.
 */
const idempotencyHeader = (key: string) => ({ headers: { "Idempotency-Key": key } });

export const generateIdempotencyKey = (): string => randomId();

// ── API surface ────────────────────────────────────────────────────────────

export const walletsApi = {
  getByCustomer: async (customerId: string): Promise<Wallet> => {
    const { data } = await apiClient.get<ApiResponse<Wallet> | Wallet>(
      `/wallets/customers/${customerId}`
    );
    return unwrap<Wallet>(data);
  },

  getStatement: async (
    customerId: string,
    params?: { from?: string; to?: string; pageNumber?: number; pageSize?: number }
  ): Promise<WalletLedgerEntry[]> => {
    const { data } = await apiClient.get<ApiResponse<WalletLedgerEntry[]> | WalletLedgerEntry[]>(
      `/wallets/customers/${customerId}/statement`,
      { params }
    );
    return unwrap<WalletLedgerEntry[]>(data);
  },

  getPending: async (params?: {
    type?: string;
    customer?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<WalletLedgerEntry[]> => {
    const { data } = await apiClient.get<ApiResponse<WalletLedgerEntry[]> | WalletLedgerEntry[]>(
      `/wallets/pending`,
      { params }
    );
    return unwrap<WalletLedgerEntry[]>(data);
  },

  createTopup: async (
    payload: CreateWalletTopupRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/topups`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  createCashOut: async (
    payload: CreateWalletCashOutRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/cashouts`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  createAdjustment: async (
    payload: CreateWalletAdjustmentRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/adjustments`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  applyToOrder: async (
    payload: ApplyWalletToOrderRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/apply-to-order`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  approve: async (
    entryId: string,
    payload: ApproveWalletEntryRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/entries/${entryId}/approve`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  reject: async (
    entryId: string,
    payload: RejectWalletEntryRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/entries/${entryId}/reject`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },

  reverse: async (
    entryId: string,
    payload: ReverseWalletEntryRequest,
    idempotencyKey: string
  ): Promise<WalletLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<WalletLedgerEntry> | WalletLedgerEntry>(
      `/wallets/entries/${entryId}/reverse`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<WalletLedgerEntry>(data);
  },
};
